import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/styles/ContactPage.css';
import '../assets/styles/EnhancedContactPage.css';

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  newsletter: boolean;
}

interface ValidationErrors {
  [key: string]: string[];
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    newsletter: false
  });
  
  const [formStatus, setFormStatus] = useState<{
    submitted: boolean;
    success: boolean;
    message: string;
    loading: boolean;
  }>({
    submitted: false,
    success: false,
    message: '',
    loading: false
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationErrors({});
    
    // Set loading state
    setFormStatus({
      submitted: false,
      success: false,
      message: '',
      loading: true
    });
    
    try {
      // Convert formData to URL encoded format
      // Note: URLSearchParams doesn't directly work with complex objects, so we need to manually build it
      const formDataForSubmission = new URLSearchParams();
      
      // Add each field to the URLSearchParams object
      Object.entries(formData).forEach(([key, value]) => {
        // Convert boolean to string for URL encoding
        const stringValue = typeof value === 'boolean' ? value.toString() : value;
        formDataForSubmission.append(key, stringValue);
      });
      
      // Send data to the API
      const response = await fetch('https://api.trader.studio/contact/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataForSubmission.toString()
      });
      
      // Handle the response
      if (response.ok) {
        const data = await response.json();
        
        setFormStatus({
          submitted: true,
          success: true,
          message: data.message || 'Thank you for your message. Our team will contact you shortly.',
          loading: false
        });
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
          newsletter: false
        });
      } else {
        const errorData = await response.json();
        
        // Check if the API returned field-specific validation errors
        if (errorData.errors && typeof errorData.errors === 'object') {
          setValidationErrors(errorData.errors);
          setFormStatus({
            submitted: true,
            success: false,
            message: 'Please correct the errors in the form.',
            loading: false
          });
        } else {
          throw new Error(errorData.message || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      setFormStatus({
        submitted: true,
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later.',
        loading: false
      });
    }
  };

  // Function to get field error message
  const getFieldError = (fieldName: string): string | null => {
    if (validationErrors[fieldName] && validationErrors[fieldName].length > 0) {
      return validationErrors[fieldName][0];
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>Contact Translate Stocks | Get Support & Information</title>
        <meta name="description" content="Contact the Translate Stocks team for sales inquiries, technical support, or to schedule a demo of our AI-powered trading platform." />
        <meta name="keywords" content="translate stocks contact, AI trading support, price on rails demo, trading platform help" />
      </Helmet>
      
      <Header />
      
      <main className="contact-page">
        <section className="hero-section contact-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Get in <span className="highlight">Touch</span></h1>
              <p className="hero-subtitle">
                Have questions about Price on Rails or need support with your account? 
                Our team is here to help you get the most from our trading platform.
              </p>
            </div>
          </div>
        </section>
        
        <section className="contact-options meeting-focus">
          <div className="container">
            <div className="booking-header">
              <h2>Schedule a <span className="highlight">Personal Consultation</span></h2>
              <p>Speak directly with our expert team to explore how Price on Rails can transform your trading strategy</p>
            </div>
            <div className="booking-card">
              <div className="booking-card-content">
                <div className="booking-icon">
                  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Personalized Video Call</h3>
                <div className="booking-features">
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>One-on-one consultation</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Live platform demonstration</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>30-minute dedicated session</span>
                  </div>
                </div>
                <div className="booking-cta">
                  <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                    Schedule Your Call
                  </a>
                  <p className="booking-note">Choose a time that works for you</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="contact-info-section">
          <div className="container">
            <div className="contact-info-grid">
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Call Us Directly</h3>
                <div className="contact-info-content">
                  <a href="tel:+13213845878" className="phone-link">+1 (321) 384-5878</a>
                  <p className="contact-info-note">Our team is available Monday-Friday</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="contact-form-section">
          <div className="container">
            <div className="form-wrapper">
              <div className="form-header">
                <h2>Send Us a Message</h2>
                <p>Complete the form below and we'll get back to you as soon as possible.</p>
              </div>
              
              {formStatus.submitted && (
                <div className={`form-message ${formStatus.success ? 'success' : 'error'}`}>
                  {formStatus.message}
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name*</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required 
                      className={getFieldError('name') ? 'error' : ''}
                    />
                    {getFieldError('name') && (
                      <p className="error-message">{getFieldError('name')}</p>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address*</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      className={getFieldError('email') ? 'error' : ''}
                    />
                    {getFieldError('email') && (
                      <p className="error-message">{getFieldError('email')}</p>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={getFieldError('phone') ? 'error' : ''}
                    />
                    {getFieldError('phone') && (
                      <p className="error-message">{getFieldError('phone')}</p>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company">Company/Organization</label>
                    <input 
                      type="text" 
                      id="company" 
                      name="company" 
                      value={formData.company}
                      onChange={handleInputChange}
                      className={getFieldError('company') ? 'error' : ''}
                    />
                    {getFieldError('company') && (
                      <p className="error-message">{getFieldError('company')}</p>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject*</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className={getFieldError('subject') ? 'error' : ''}
                  >
                    <option value="">Select a subject</option>
                    <option value="Sales Inquiry">Sales Inquiry</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Partnership Opportunity">Partnership Opportunity</option>
                    <option value="Demo Request">Demo Request</option>
                    <option value="Press Inquiry">Press Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                  {getFieldError('subject') && (
                    <p className="error-message">{getFieldError('subject')}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Your Message*</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className={getFieldError('message') ? 'error' : ''}
                  ></textarea>
                  {getFieldError('message') && (
                    <p className="error-message">{getFieldError('message')}</p>
                  )}
                </div>
                
                <div className="form-group checkbox">
                  <input 
                    type="checkbox" 
                    id="newsletter" 
                    name="newsletter"
                    checked={formData.newsletter}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="newsletter">
                    <strong>Subscribe to our premium newsletter</strong>
                    <span className="subscription-description">
                      Get exclusive access to trading insights, feature updates, and market analysis delivered to your inbox
                    </span>
                  </label>
                </div>
                
                <div className="form-submit">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-large"
                    disabled={formStatus.loading}
                  >
                    {formStatus.loading ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
        
        {/*}
        <section className="faq-section">
          <div className="container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            
            <div className="faq-grid">
              <div className="faq-item">
                <h3>How can I schedule a demo of Price on Rails?</h3>
                <p>
                  You can schedule a personalized demo by contacting our sales team at 
                  <a href="mailto:sales@translatestocks.com"> sales@translatestocks.com</a> or by filling out the 
                  contact form above and selecting "Demo Request" as your subject.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>What trading platforms does Price on Rails integrate with?</h3>
                <p>
                  Our platform currently integrates with major brokerages including Interactive Brokers, 
                  TD Ameritrade, E*TRADE, and more. We're constantly adding new integrations. Contact our 
                  support team for specific compatibility questions.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>How do I get technical support for my account?</h3>
                <p>
                  Technical support is available via email at support@translatestocks.com or by phone 
                  at +1 (800) 555-1001. Premium account holders also have access to dedicated support channels.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Do you offer educational resources for new users?</h3>
                <p>
                  Yes! We provide comprehensive documentation, video tutorials, and regular webinars to 
                  help you get the most out of our platform. Visit our Learning Center at 
                  learn.translatestocks.com to access these resources.
                </p>
              </div>
            </div>
          </div>
        </section>
        */}
      </main>
      
    </>
  );
};

export default ContactPage;